<script lang="ts" setup>
import type { Placement } from '../../ideo/ui-popover/types';
import { resolveConfirmationTeleportTarget, type ConfirmationTeleportTarget } from './teleportTarget';

defineOptions({
    name: 'Confirmation'
});

const props = withDefaults(defineProps<{
    value: any;
    target: any;
    message: string;
    placement?: Placement;
    id?: string | null;
    wrapperClass?: Record<string, boolean> | string[] | string;
    actionClass?: Record<string, boolean> | string[] | string;
}>(), {
    placement: 'auto',
    wrapperClass: '',
    actionClass: '',
    id: null
});

const emit = defineEmits<{
    (e: 'confirm', value: any): void;
    (e: 'cancel'): void;
}>();

const popover = ref(null);
const teleportTo = ref<ConfirmationTeleportTarget>('body');

async function syncTeleportTarget()
{
    await nextTick();

    teleportTo.value = resolveConfirmationTeleportTarget(props.target);
}

const open = (target?: HTMLElement) =>
{
    popover.value.open(target);
};

const close = () =>
{
    popover.value.close();
};

const confirm = () =>
{
    close();
    emit('confirm', props.value);
};

const cancel = () =>
{
    close();
    emit('cancel');
};

const wrapperClasses = computed(() => ({
    'confirmation-content': true,
    ...normalizeClasses(props.wrapperClass)
}));

const actionClasses = computed(() => ({
    'confirmation-actions': true,
    ...normalizeClasses(props.actionClass)
}));

defineExpose({
    open
});

onMounted(syncTeleportTarget);

watch(() => props.target, syncTeleportTarget);
</script>

<template>
    <Teleport :to="teleportTo">
        <IdeoPopover
            :id="id"
            ref="popover"
            class="confirmation-popover"
            :target="target"
            :placement="placement"
            triggers="blur click"
            variant="confirmation"
            @pointerdown.stop
            @mousedown.stop
            @mouseup.stop
            @click.stop
        >
            <div :class="wrapperClasses">
                <div class="confirmation-message min-w-0 text-start text-[0.8125rem]/normal font-medium" v-html="message.replaceAll('\n', '<br>')" />
                <IdeoButtonGroup :class="actionClasses">
                    <IdeoButton class="w-10" variant="secondary" size="xs" @mousedown.stop.prevent="cancel">
                        {{ $t('[[[Nie]]]') }}
                    </IdeoButton>
                    <IdeoButton class="w-10" variant="danger" size="xs" @mousedown.stop.prevent="confirm">
                        {{ $t('[[[Tak]]]') }}
                    </IdeoButton>
                </IdeoButtonGroup>
            </div>
        </IdeoPopover>
    </Teleport>
</template>

<style scoped>
.confirmation-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    inline-size: fit-content;
    max-inline-size: min(360px, calc(100vw - 48px));
}

.confirmation-message {
    flex: 1 1 max-content;
    min-inline-size: min(18ch, 100%);
    max-inline-size: 100%;
    font-weight: 600;
    text-align: start;
    overflow-wrap: break-word;
}

.confirmation-actions {
    flex: 0 0 auto;
    justify-content: flex-end;
    margin-inline-start: auto;

    .btn {
        min-height: 20px;
    }
}

</style>
